import type {
  IFilter,
  IFilterOption,
  IFilterValue,
  IFilterValueOption,
} from "@components/molecules/filter/types";
import type { IOption } from "@/interfaces";

export const isItemSelected = (
  index: number,
  key: string,
  value: IFilterValue[],
) => {
  return (
    (value?.[index]?.values?.findIndex((val) => val?.key === key) || -1) >= 0
  );
};

export const isAllItemSelected = (
  index: number,
  value: IFilterValue[],
  options: IFilter[],
) => {
  return value[index]?.values?.length === options[index]?.options?.length;
};

export const addItemToFilter = (
  index: number,
  filter: IFilterOption,
  state: IFilterValue[],
): IFilterValue[] => {
  return state?.map((item, i) => {
    return i === index
      ? {
          ...item,
          values: [
            ...(item?.values || []),
            { key: filter.key, value: filter.value },
          ],
        }
      : { ...item };
  });
};

export const removeItemFromFilter = (
  index: number,
  filter: IFilterOption,
  state: IFilterValue[],
) => {
  return state?.map((item, i) => {
    return i === index
      ? {
          ...item,
          values: item?.values?.filter((value) => value?.key !== filter?.key),
        }
      : { ...item };
  });
};

export const clearAllFilters = (
  onFilterSelect?: (filters: IFilterValue[]) => void,
) => {
  onFilterSelect?.([]);
};

export const addAllItemsToFilter = (
  index: number,
  options: IFilter[],
  state: IFilterValue[],
  onFilterSelect?: (filters: IFilterValue[]) => void,
) => {
  const isAllItemsSelected = isAllItemSelected(index, state, options);

  if (isAllItemsSelected) {
    clearAllFilters(onFilterSelect);
    return;
  }
  const all = state?.map((item, i) => {
    return i === index
      ? {
          ...item,
          values: [...(options[index]?.options || [])],
        }
      : { ...item };
  });

  onFilterSelect?.(all);
};

export const handleClickFilterOption = (
  index: number,
  filter: IFilterOption,
  value: IFilterValue[],
  isAdding: boolean,
  onFilterSelect?: (filters: IFilterValue[]) => void,
) => {
  if (isAdding) {
    onFilterSelect?.(addItemToFilter(index, filter, value));
  } else {
    onFilterSelect?.(removeItemFromFilter(index, filter, value));
  }
};

export const normalizeFilterValues = (
  items: IFilter[],
  values: IFilterValue[],
): IFilterValue[] => {
  return items?.map((item, index) => {
    const value = values[index];

    return {
      ...value,
      key: value?.key || item?.key,
      title: value?.title || item?.title,
      values: value?.values || [],
    };
  });
};

export const getParamName = (paramName: string, index?: string | number) => {
  return paramName + (index || "");
};
export const getFilterParamName = (index?: string | number) => {
  return getParamName("filters", index);
};

export function flattenJsonToQueryParam(
  jsonString: string,
  parentKey = "",
): string {
  // TODO CONVERT THEN SET
  try {
    const jsonObject = JSON.parse(jsonString);
    const params: string[] = [];

    recurse(params, jsonObject, parentKey);

    return params.join("-");
  } catch {
    return "";
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function recurse(params: string[], current: any, parent: string) {
  for (const key in current) {
    const value = current[key];
    const paramName = parent ? `${parent}.${key}` : key;

    if (typeof value === "object") {
      recurse(params, value, paramName);
    } else {
      params.push(`${paramName}~${value}`);
    }
  }
}

export function queryParamsToNestedJson(queryParams: string) {
  const paramsArray = queryParams.split("-");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const jsonObject: Record<string, any> = {};

  for (const param of paramsArray) {
    const [paramName, value] = param.split("~");
    const keys = paramName?.split(".") || [];

    let currentObject = jsonObject;

    for (let i = 0; i < keys.length; i++) {
      const key = keys?.[i] || "";

      if (i === keys.length - 1) {
        currentObject[key] = value;
      } else {
        if (!currentObject[key]) {
          currentObject[key] = {};
        }
        currentObject = currentObject[key];
      }
    }
  }

  const result: IFilterValue[] = [];

  for (const key in jsonObject) {
    const value = jsonObject[key];

    if (!value) {
      return [];
    }
    const { title, values } = value;
    const valueArray: IFilterValueOption[] = [];

    for (const key in values) {
      const value = values[key];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      valueArray.push(value);
    }

    const filterValue: IFilterValue = {
      key,
      title,
      values: valueArray,
    };

    result.push(filterValue);
  }

  return result;
}

export const filterToObjectMap = <
  TOptions extends IFilter[],
  TValues extends IFilterValue[],
>(
  options: TOptions,
  values: TValues,
) => {
  interface IVariantMap {
    basic: string[];
    timespan: { from: Date | undefined; to: Date | undefined };
    cost: { costFrom: string | undefined; costTo: string | undefined };
    dropdown?: { dropdown?: IOption | undefined };
  }

  type IKey =
    | keyof TOptions[number]["key"]
    | "from"
    | "to"
    | "costFrom"
    | "costTo"
    | "dropdown";

  type IResult = Record<IKey, IVariantMap[TOptions[number]["variant"]]>;

  const object: Partial<IResult> = {};

  if (options)
    for (const [index, option] of options.entries()) {
      const key = option?.key as IKey;
      const value = values[index];

      const isValueExist = value?.values && value?.values?.length > 0;

      if ((!option?.variant || option?.variant === "basic") && isValueExist) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object[key] = value?.values?.map((val) => val?.key) as any;
      }

      if (option?.variant === "timespan" && isValueExist) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object.from = value?.values[1]?.from as any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object.to = value?.values[1]?.to as any;
      }

      if (option?.variant === "cost" && isValueExist) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object.costFrom = value?.values[1]?.costFrom as any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object.costTo = value?.values[1]?.costTo as any;
      }

      if (option?.variant === "dropdown" && isValueExist) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object.dropdown = value?.values[1]?.dropdown as any;
      }
    }

  return object;
};
